import { Transaction } from "../models/Transaction";
import { TxData } from "../models/Block";

export const formatTime = (time: number): string => {
  const units = [
    { label: "days", divisor: 24 * 60 * 60 * 1000 },
    { label: "hours", divisor: 60 * 60 * 1000 },
    { label: "minutes", divisor: 60 * 1000 },
    { label: "seconds", divisor: 1000 },
  ];

  return units
    .map(({ label, divisor }) => {
      const value = Math.floor(time / divisor);
      time %= divisor;
      return value > 0 ? `${value} ${label}` : "";
    })
    .filter(Boolean)
    .join(" ");
};

export function formatCalendarDate(date: Date): string {
  return date.toISOString().replace(/-|:|\.\d+/g, "");
}

/**
 * Function which return blockTime if it exists otherwise return chainTime which is deprecated.
 * @param val
 */
export function useAvailableTime(val: Transaction | TxData): string | null {
  if (!val) return null;

  if (val.blockTime) {
    return val.blockTime;
  } else if (val.chainTime) {
    return val.chainTime;
  } else {
    return null;
  }
}

/**
 * Formats a date and returns its string representation along with its UTC offset.
 * @param {Date} date - The date to be formatted.
 * @param {string} timeZone - The corret UTC timezone.
 * @returns {Object} An object containing formattedDateTime and utcOffset properties.
 * @property {string} formattedDateTime - The formatted date and time string (e.g., "03/07/2024, 13:15:56").
 * @property {string} utcOffset - The UTC offset string (e.g., "UTC+3").
 * @example
 * const formatted = formatDate(new Date(),"Europe/Ljubljana");
 * console.log(formatted.formattedDateTime); // Outputs: 03/07/2024, 13:15:56
 * console.log(formatted.utcOffset); // Outputs: UTC+1
 */
export function formatDate(
  date: Date,
  timeZone?: string
): { formattedDateTime: string; utcOffset: string } {
  // Če ni podan, vzemi uporabnikov lokalni časovni pas
  const zone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formattedDateTime = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    dateStyle: "short",
    timeStyle: "medium",
  }).format(date);

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    timeZoneName: "shortOffset",
  }).formatToParts(date);

  const offsetPart = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  const utcOffset = offsetPart.replace("GMT", "UTC").replace(" ", "");

  return { formattedDateTime, utcOffset };
}

/**
 * Converts a Unix timestamp to an ISO string representation.
 * @param {number} unixTimestamp - The Unix timestamp to be converted (in seconds).
 * @returns {string | null} The ISO string representation of the date (e.g., "2024-03-07T13:15:56Z") or null if the timestamp is falsy.
 * @example
 * const isoDate = formatToISO(1718643851);
 * console.log(isoDate); // Outputs: "2024-03-07T13:15:51Z"
 * 
 * @note The Unix timestamp is assumed to be in seconds, so it is multiplied by 1000 to convert to milliseconds.
 */
export function formatToISO(unixTimestamp: number): string | null {
  if (!unixTimestamp) return null;
  return new Date(unixTimestamp * 1000).toISOString().replace(/\.\d{3}Z$/, "Z");
}

/**
 * Converts a Date object to Unix timestamp (seconds since 1970-01-01).
 * 
 * @param date - The Date object to be converted.
 * 
 * @returns A Unix timestamp representing the date in seconds.
 */
export function convertToUnixTimestamp(date: Date): number {
  return Math.floor(date.getTime() / 1000);
}
